(() => {
  const formSelector = '.js-native-contact-form'
  const customMailerSelector = '.js-custom-mailer-form'
  const customMailerEndpoint = '/apps/stepahead-contact'

  const fallbackErrorMessages = {
    contact: '送信できませんでした。入力内容をご確認のうえ、時間をおいて再度お試しください。',
    wholesale: '送信できませんでした。入力内容をご確認のうえ、時間をおいて再度お試しください。',
  }

  const errorCodeMessages = {
    invalid_request: '送信内容を読み取れませんでした。ページを再読み込みして再度お試しください。',
    missing_signature: '送信元を確認できませんでした。ページを再読み込みして再度お試しください。',
    invalid_signature: '送信元を確認できませんでした。ページを再読み込みして再度お試しください。',
    stale_timestamp: '送信の有効期限が切れました。ページを再読み込みして再度お試しください。',
    shop_not_allowed: 'このストアからの送信は現在受け付けていません。',
    rate_limited: '送信回数が多すぎます。時間をおいて再度お試しください。',
    server_misconfigured: '送信設定を確認中です。時間をおいて再度お試しください。',
    mailer_error: 'メール送信に失敗しました。時間をおいて再度お試しください。',
    delivery_failed: '送信を完了できませんでした。時間をおいて再度お試しください。',
    duplicate: '同じ内容が直前に送信されています。時間をおいて再度お試しください。',
    network_error: '通信に失敗しました。接続状況をご確認のうえ、再度お試しください。',
  }

  const fallbackFieldLabels = {
    form_type: 'フォーム種別',
    name: 'お名前',
    email: 'メールアドレス',
    phone: '電話番号',
    topic: 'お問い合わせ種別',
    inquiry_type: 'お問い合わせの種類',
    company: '会社名・組織名',
    order_number: '注文番号',
    store: '対象店舗',
    product: '商品名 / 商品 URL',
    body: 'お問い合わせ内容',
    privacy_consent: '個人情報保護方針への同意',
  }

  function bindForm(form) {
    if (!(form instanceof HTMLFormElement) || form.dataset.formsBound === 'true') {
      return
    }

    if (form.matches(customMailerSelector)) {
      // Shopify contact remains the no-script fallback; active clients point custom mode at the App Proxy.
      form.setAttribute('action', customMailerEndpoint)
    }

    form.dataset.formsBound = 'true'

    form.addEventListener('keydown', (event) => {
      const target = event.target

      if (!(target instanceof HTMLTextAreaElement)) {
        return
      }

      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault()
        form.requestSubmit()
      }
    })

    form.addEventListener('submit', (event) => {
      if (!form.checkValidity()) {
        return
      }

      if (form.matches(customMailerSelector)) {
        event.preventDefault()
        submitCustomMailerForm(form)
        return
      }

      setSubmitButtonPending(form, true)
    })
  }

  async function submitCustomMailerForm(form) {
    const payload = buildCustomMailerPayload(form)

    clearCustomMailerMessages(form)
    setFormPending(form, true)

    try {
      const response = await fetch(customMailerEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      const result = await readJsonResponse(response)

      if (response.ok && result?.ok) {
        showCustomMailerSuccess(form)
        form.reset()
        return
      }

      showCustomMailerErrors(form, result, response.status, response.headers.get('x-request-id'))
    } catch {
      showCustomMailerErrors(form, { code: 'network_error' })
    } finally {
      setFormPending(form, false)
    }
  }

  function buildCustomMailerPayload(form) {
    const formData = new FormData(form)
    const payload = {
      contact_me_by_fax: '',
      source_path: window.location.pathname,
      source_url: window.location.href,
      source_title: document.title,
    }

    formData.forEach((value, key) => {
      if (value instanceof File) {
        return
      }

      const field = normalizeFieldName(key)
      if (!field || field === 'tags') {
        return
      }

      if (field === 'privacy_consent') {
        payload[field] = normalizeBoolean(value)
        return
      }

      payload[field] = String(value).trim()
    })

    if (!payload.form_type) {
      payload.form_type = inferFormType(form)
    }

    if (payload.form_type === 'wholesale-inquiry') {
      payload.form_type = 'wholesale'
    }

    if (payload.privacy_consent === undefined) {
      payload.privacy_consent = false
    }

    if (payload.contact_me_by_fax === undefined) {
      payload.contact_me_by_fax = ''
    }

    return payload
  }

  async function readJsonResponse(response) {
    const contentType = response.headers.get('content-type') || ''

    if (!contentType.includes('application/json')) {
      return null
    }

    try {
      return await response.json()
    } catch {
      return null
    }
  }

  function showCustomMailerSuccess(form) {
    clearCustomMailerMessages(form)

    const successMessage = form.querySelector('[data-custom-mailer-success]')
    if (successMessage instanceof HTMLElement) {
      successMessage.hidden = false
      focusMessage(successMessage)
    }
  }

  function showCustomMailerErrors(form, result, status, requestId) {
    const formType = inferFormType(form)
    const errorBox = form.querySelector('[data-custom-mailer-errors]')
    const errorList = form.querySelector('[data-custom-mailer-error-list]')
    const errors = normalizeErrors(result, status, formType)

    clearCustomMailerMessages(form)
    applyFieldErrors(form, errors)

    if (errorList instanceof HTMLElement) {
      errorList.replaceChildren()

      errors.forEach((error) => {
        const item = document.createElement('li')
        item.textContent = formatErrorMessage(form, error)
        errorList.append(item)
      })

      const shortRequestId = normalizeRequestId(requestId)
      if (shortRequestId) {
        const item = document.createElement('li')
        item.textContent = `お問い合わせ番号: ${shortRequestId.slice(0, 8)}`
        errorList.append(item)
      }
    }

    if (errorBox instanceof HTMLElement) {
      errorBox.hidden = false
      focusMessage(errorBox)
    }
  }

  function normalizeRequestId(value) {
    return typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(value)
      ? value
      : ''
  }

  function normalizeErrors(result, status, formType) {
    if (Array.isArray(result?.errors) && result.errors.length > 0) {
      return result.errors.map((error) => ({
        field: normalizeFieldName(error.field || ''),
        message: String(error.message || ''),
      }))
    }

    const code = result?.code || (status ? `status_${status}` : 'network_error')
    return [{ field: '', message: errorCodeMessages[code] || fallbackErrorMessages[formType] }]
  }

  function formatErrorMessage(form, error) {
    if (!error.field) {
      return error.message || fallbackErrorMessages[inferFormType(form)]
    }

    const label = getFieldLabel(form, error.field)
    const message = error.message || ''

    if (message === 'Required.') {
      return `${label}を入力してください。`
    }

    if (message === 'Invalid email address.') {
      return 'メールアドレスを正しく入力してください。'
    }

    if (message === 'Privacy consent is required.') {
      return '個人情報保護方針への同意が必要です。'
    }

    if (message === 'Invalid topic.' || message === 'Invalid inquiry type.') {
      return `${label}を選択し直してください。`
    }

    const lengthMatch = message.match(/^Must be (\d+) characters or fewer\.$/)
    if (lengthMatch) {
      return `${label}は${lengthMatch[1]}文字以内で入力してください。`
    }

    return `${label}: ${message || fallbackErrorMessages[inferFormType(form)]}`
  }

  function applyFieldErrors(form, errors) {
    const renderedFields = new Set()

    errors.forEach((error) => {
      if (!error.field || renderedFields.has(error.field)) {
        return
      }

      const controls = getControlsForField(form, error.field).filter((control) => {
        return control instanceof HTMLElement && control.getAttribute('type') !== 'hidden'
      })
      const firstControl = controls[0]

      if (!firstControl) {
        return
      }

      const fieldContainer = firstControl.closest('.ct-field, .ws-field')
      if (!(fieldContainer instanceof HTMLElement)) {
        return
      }

      renderedFields.add(error.field)

      const fieldError = document.createElement('p')
      const variant = getFormVariant(form)
      const fallbackId = `${form.id || variant}-field-${error.field}`
      const errorId = `${firstControl.id || fallbackId}-error`

      fieldError.className = `${variant}-field-error`
      fieldError.id = errorId
      fieldError.dataset.formFieldError = 'true'
      fieldError.dataset.field = error.field
      fieldError.textContent = formatErrorMessage(form, error)
      fieldContainer.append(fieldError)

      controls.forEach((control) => {
        control.setAttribute('aria-invalid', 'true')
        appendDescribedBy(control, errorId)
      })
    })
  }

  function clearCustomMailerMessages(form) {
    const successMessage = form.querySelector('[data-custom-mailer-success]')
    const errorBox = form.querySelector('[data-custom-mailer-errors]')
    const errorList = form.querySelector('[data-custom-mailer-error-list]')

    if (successMessage instanceof HTMLElement) {
      successMessage.hidden = true
    }

    if (errorBox instanceof HTMLElement) {
      errorBox.hidden = true
    }

    if (errorList instanceof HTMLElement) {
      errorList.replaceChildren()
    }

    form.querySelectorAll('[data-form-field-error]').forEach((fieldError) => {
      const field = fieldError instanceof HTMLElement ? fieldError.dataset.field : ''
      if (field) {
        getControlsForField(form, field).forEach((control) => {
          control.removeAttribute('aria-invalid')
          removeDescribedBy(control, fieldError.id)
        })
      }

      fieldError.remove()
    })
  }

  function setFormPending(form, isPending) {
    form.dataset.submitting = isPending ? 'true' : 'false'
    form.setAttribute('aria-busy', isPending ? 'true' : 'false')

    form.querySelectorAll('input, select, textarea, button').forEach((control) => {
      if (!(control instanceof HTMLInputElement) &&
        !(control instanceof HTMLSelectElement) &&
        !(control instanceof HTMLTextAreaElement) &&
        !(control instanceof HTMLButtonElement)) {
        return
      }

      if (isPending) {
        if (control.disabled) {
          control.dataset.customMailerWasDisabled = 'true'
        } else {
          control.disabled = true
        }
        return
      }

      if (control.dataset.customMailerWasDisabled === 'true') {
        delete control.dataset.customMailerWasDisabled
      } else {
        control.disabled = false
      }
    })

    setSubmitButtonPending(form, isPending)
  }

  function setSubmitButtonPending(form, isPending) {
    const submitButton = form.querySelector('[data-form-submit]')

    if (!(submitButton instanceof HTMLButtonElement)) {
      return
    }

    const label = submitButton.querySelector('[data-submit-label]')
    if (label instanceof HTMLElement && !submitButton.dataset.defaultLabel) {
      submitButton.dataset.defaultLabel = label.textContent || ''
    }

    submitButton.disabled = isPending
    submitButton.dataset.submitting = isPending ? 'true' : 'false'

    if (label instanceof HTMLElement) {
      label.textContent = isPending
        ? submitButton.dataset.submittingLabel || '送信中...'
        : submitButton.dataset.defaultLabel || label.textContent
    }
  }

  function appendDescribedBy(control, id) {
    const existing = (control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)

    if (!existing.includes(id)) {
      existing.push(id)
      control.setAttribute('aria-describedby', existing.join(' '))
    }
  }

  function removeDescribedBy(control, id) {
    if (!id) {
      return
    }

    const next = (control.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((token) => token && token !== id)

    if (next.length) {
      control.setAttribute('aria-describedby', next.join(' '))
    } else {
      control.removeAttribute('aria-describedby')
    }
  }

  function getControlsForField(form, field) {
    return Array.from(form.elements).filter((control) => {
      if (!(control instanceof HTMLInputElement) &&
        !(control instanceof HTMLSelectElement) &&
        !(control instanceof HTMLTextAreaElement)) {
        return false
      }

      return normalizeFieldName(control.name) === field
    })
  }

  function getFieldLabel(form, field) {
    const controls = getControlsForField(form, field)
    const firstControl = controls[0]

    if (firstControl) {
      const fieldContainer = firstControl.closest('.ct-field, .ws-field')
      const label = fieldContainer?.querySelector('.ct-label, .ws-label')

      if (label instanceof HTMLElement) {
        return cleanLabelText(label.textContent || '') || fallbackFieldLabels[field] || field
      }
    }

    return fallbackFieldLabels[field] || field
  }

  function cleanLabelText(value) {
    return value
      .replace(/\s+/g, ' ')
      .replace(/\s*\*\s*/g, '')
      .replace('（任意）', '')
      .trim()
  }

  function focusMessage(element) {
    element.focus({ preventScroll: true })
    element.scrollIntoView({ block: 'nearest' })
  }

  function normalizeFieldName(name) {
    const fieldName = String(name || '')
    const contactMatch = fieldName.match(/^contact\[(.+)\]$/)

    if (contactMatch) {
      return contactMatch[1]
    }

    return fieldName
  }

  function inferFormType(form) {
    const field = getControlsForField(form, 'form_type')[0]
    const rawType = field instanceof HTMLInputElement ? field.value : ''

    if (rawType === 'wholesale-inquiry') {
      return 'wholesale'
    }

    if (rawType) {
      return rawType
    }

    return form.closest('.wholesale-page') ? 'wholesale' : 'contact'
  }

  function getFormVariant(form) {
    return form.classList.contains('ws-form') ? 'ws' : 'ct'
  }

  function normalizeBoolean(value) {
    if (value === true || value === false) {
      return value
    }

    return ['true', '1', 'on', 'yes', 'agree', 'agreed'].includes(String(value).toLowerCase())
  }

  function initForms() {
    document.querySelectorAll(formSelector).forEach(bindForm)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initForms, { once: true })
  } else {
    initForms()
  }

  document.addEventListener('shopify:section:load', initForms)
  document.addEventListener('swup:page:view', initForms)
})()
